import React from "react";
import { deepCopy, getUnique } from "../../core/helpers";
import { CARDS_PAGE_PATH } from "../../App";
import { getCategoryPath, sortMicroDrivers } from "../../App.helpers";

export const timeChartLegendData = [
    {
        name: 'At home',
        type: 'round',
        color: '#7E57C2',
    },
    {
        name: 'Away home',
        type: 'round',
        color: '#FB8C00',
    },
];

function getHeaderData(data) {
    return data.headerData;
}

const obtainedFoodInitialData = [
    {
        name: 'already had food on hand',
        value: 20,
    },
    {
        name: 'used a delivery service',
        value: 10,
    },
    {
        name: 'brought friends or family',
        value: 35,
    },
    {
        name: 'went somewhere to get it',
        value: 5,
    },
    {
        name: 'other',
        value: 15,
    },
];

const foodSourceInitialData = [
    {
        name: 'From home & AFH',
        value: 32,
        color: '#222',
    },
    {
        name: 'sourced from home',
        value: 32,
        color: '#222',
    },
    {
        name: 'sourced AFH',
        value: 68,
        color: '#42A5F5',
        events: [
            {
                name: 'retail FS',
                value: 36,
                color: '#42A5F5',
            },
            {
                name: 'on-site',
                value: 36,
                color: '#42A5F5',
            },
            {
                name: 'restaurant',
                value: 28,
                color: '#4CAF50',
                events: [
                    {
                        name: 'large chain',
                        value: 20,
                        color: '#4CAF50',
                    },
                    {
                        name: 'small chain',
                        value: 20,
                        color: '#4CAF50',
                    },
                    {
                        name: 'independent',
                        value: 60,
                        color: '#4CAF50',
                    },
                ]
            },
        ]
    },
];

function getFoodSourceData(data) {
    const whereData = data.where;
    const result = deepCopy(foodSourceInitialData);
    result[0].value = whereData.homeAway.both;
    result[1].value = whereData.homeAway.atHome;
    result[2].value = whereData.homeAway.awayHome;
    result[2].events[0].value = whereData.afhEvents.retail;
    result[2].events[1].value = whereData.afhEvents.onSite;
    result[2].events[2].value = whereData.afhEvents.restaurant;
    result[2].events[2].events[0].value = whereData.restaurantKind.large;
    result[2].events[2].events[1].value = whereData.restaurantKind.small;
    result[2].events[2].events[2].value = whereData.restaurantKind.independent;

    return result;
}

function getSegmentShareData(data) {
    const rawData = data.where.segment;
    const categories = getUnique(rawData.map(({ label }) => label));
    return categories.map((categoryName, index) => ({
        name: categoryName,
        color: index === 0 ? '' : 'yellow',
        items: rawData
            .filter(item => item.label === categoryName)
            .map(item => ({
                name: item.name,
                value: item.cameFrom,
                total: item.cameFromTotal,
                link: `${CARDS_PAGE_PATH}/${getCategoryPath(1)}/${item.id}`,
            })),
    }));
}

function getObtainedData(data) {
    const obtainedData = data.where.obtained;
    const result = deepCopy(obtainedFoodInitialData);
    result[0].value = obtainedData.haveFood;
    result[1].value = obtainedData.deliveryService;
    result[2].value = obtainedData.broughtByPeer;
    result[3].value = obtainedData.goElsewhere;
    result[4].value = obtainedData.other;

    return result;
}

function getMegaDriversData(data) {
    return data.why.megaDrivers
        .map(({ formatValue, formatValueTotal, name, id }) => ({
            name,
            value: formatValue,
            total: formatValueTotal,
            id,
            link: `${CARDS_PAGE_PATH}/${getCategoryPath(4)}/${id}`,
        }))
}

function getMicroDriversData(data) {
    const microDriversRawData = data.why.microDrivers;
    const result = [];

    const subCategories = getUnique(microDriversRawData.map(({ header }) => header))
        .sort(sortMicroDrivers);

    subCategories.forEach((name) => {
        result.push({
            name,
            items: microDriversRawData
                .filter(({ header }) => header === name)
                .map(({ label, formatValue, id, index }) => ({
                    name: label,
                    value: formatValue,
                    index,
                    id,
                    link: `${CARDS_PAGE_PATH}/${getCategoryPath(4)}/${id}`,
                })),
        });
    });

    return result;
}

function getHighlyIndexedNeeds(data) {
    const microDriversRawData = data.why.microDrivers;
    microDriversRawData.sort((a, b) => a.index > b.index ? -1 : 1);
    return microDriversRawData
        .slice(0, 10)
        .map(({ label, id, index }) => ({
            name: label,
            id,
            value: index,
            link: `${CARDS_PAGE_PATH}/${getCategoryPath(4)}/${id}`,
        }));
}

function getTopSpecificNeeds(data) {
    const microDriversRawData = data.why.microDrivers;
    microDriversRawData.sort((a, b) => a.formatValue > b.formatValue ? -1 : 1);
    return microDriversRawData
        .slice(0, 20)
        .map(({ label, id, formatValue }) => ({
            name: label,
            id,
            value: `${formatValue}%`,
            link: `${CARDS_PAGE_PATH}/${getCategoryPath(4)}/${id}`,
        }));
}

function getOccasionsData(data) {
    return data.when.occasion
        .map(({ formatValue, formatValueTotal, name, id }) => ({
            name,
            value: formatValue,
            total: formatValueTotal,
            id,
            link: `${CARDS_PAGE_PATH}/${getCategoryPath(2)}/${id}`,
        }))
}

function getTriggersData(data) {
    return data.why.triggers
        .map(({ formatValue, formatValueTotal, name, id }) => ({
            name,
            value: formatValue,
            total: formatValueTotal,
            id,
            link: `${CARDS_PAGE_PATH}/${getCategoryPath(3)}/${id}`,
        }))
}

function getTimeChartData(data) {
    const { atHome, awayHome } = data.when.timeOfDay;

    return [
        {
            color: 'rgb(126, 87, 194)',
            fillColors: ['rgba(126, 87, 194, 0.2)', 'rgba(126, 87, 194, 0)'],
            data: [atHome.morning, atHome.midday, atHome.afternoon, atHome.evening, atHome.lateNight],
        },
        {
            color: 'rgb(251, 140, 0)',
            fillColors: ['rgba(251, 140, 0, 0.2)', 'rgba(251, 140, 0, 0)'],
            data: [awayHome.morning, awayHome.midday, awayHome.afternoon, awayHome.evening, awayHome.lateNight],
        },
    ];
}

function getTimeTotalChartData(data) {

    const { total } = data.when.timeOfDay;

    return [
        {
            color: 'rgba(56, 142, 60, 1)',
            fillColors: ['rgba(56, 142, 60, 0.2)', 'rgba(56, 142, 60, 0)'],
            data: [total.morning, total.midday, total.afternoon, total.evening, total.lateNight]
        }
    ]
}

function getWhereTableData(data) {
    const whereTableRawData = data.where.decisionLocation;
    const { '0': first, ...columnsData } = whereTableRawData.columns;
    const columns = Object.values(columnsData);
    const rowsData = whereTableRawData.rows;

    return {
        columns,
        rows: [
            rowsData
                .filter(({ rowId, name }) => rowId === 1 && name.toLowerCase() !== 'where'),
            rowsData
                .filter(({ rowId, name }) => rowId === 2 && name.toLowerCase() !== 'where')
        ]
    };
}

function getDecisionPointData(data) {
    const whereTableRawData = getWhereTableData(data);
    return whereTableRawData.rows[0]
        .map(({ name, formatValue: value }) => ({
            name,
            value: `${value}%`
        }));
}

function getConsumptionPointData(data) {
    const whereTableRawData = getWhereTableData(data);
    return whereTableRawData.rows[1]
        .map(({ name, formatValue: value }) => ({
            name,
            value: `${value}%`
        }));
}

function getTimeChartSummaryData(data) {
    const rawData = data.when.timeOfDay;
    return [
        { weekend: rawData.weekendDayHome, weekday: rawData.weekDayHome },
        { weekend: rawData.weekendDayAway, weekday: rawData.weekDayAway },
    ];
}

function getSequenceData(data) {
    return data.sequence.map(({ first, second, third, fourth }) => ({
        ...first,
        subItems: [second, third, fourth],
    }));
}

function getFoodTypeConsumedCategoryData(categoryLabel) {
    switch (categoryLabel) {
        case "snacks": {
            return {
                id: 1,
                name: 'Snacks & Treats',
            };
        }
        case "breakfast": {
            return {
                id: 2,
                name: 'Breakfast',
            };
        }
        case "starters": {
            return {
                id: 3,
                name: 'Starters & Sides',
            };
        }
        case "desert": {
            return {
                id: 4,
                name: 'Dessert Items',
            };
        }
        case "mainItems": {
            return {
                id: 5,
                name: 'Main Items',
            };
        }
        default: {
            break;
        }
    }
    return null;
}

function getCravingsData(data) {
    const { data: rawItemsData, ...rawData } = data.cravings;
    return {
        ...rawData,
        items: rawItemsData.map(item => ({
            name: item.name,
            value: item.formatValue,
            total: item.formatValueTotal,
        })),
    };
}

function getFoodTypeConsumedData(data) {
    const rawData = data.foodTypeConsumed;
    return Object.entries(rawData).map(([categoryLabel, items]) => ({
        ...getFoodTypeConsumedCategoryData(categoryLabel),
        items: items.map(item => ({
            name: item.name,
            value: item.formatValue,
            total: item.formatValueTotal,
        }))
    }));
}

function getAtHomeEventsData(data) {
    const rawData = data.homeAwayEvants;
    const { effortPutIntoCooking, whoPreparedFor } = rawData;

    return [
        {
            name: 'Effort put into cooking',
            items: effortPutIntoCooking.map(item => ({
                name: item.name,
                value: item.formatValue,
            }))
        },
        {
            name: 'Who prepared for',
            items: whoPreparedFor.map(item => ({
                name: item.name,
                value: item.formatValue,
            }))
        },
    ];
}

function getAwayFromHomeEventsData(data) {
    const rawData = data.homeAwayEvants;
    const { menuType, didYouUseCoupon } = rawData;

    return [
        {
            name: 'Menu type',
            items: menuType.map(item => ({
                name: item.name,
                value: item.formatValue,
            }))
        },
        {
            name: 'Did you use a coupon',
            items: didYouUseCoupon.map(item => ({
                name: item.name,
                value: item.formatValue,
            }))
        },
    ];
}

export function normalizeData(responseData) {
    return {
        headerData: getHeaderData(responseData),
        foodSourceData: getFoodSourceData(responseData),
        decisionPointData: getDecisionPointData(responseData),
        consumptionPointData: getConsumptionPointData(responseData),
        segmentShareData: getSegmentShareData(responseData),
        obtainedData: getObtainedData(responseData),
        occasionsData: getOccasionsData(responseData),
        triggersData: getTriggersData(responseData),
        megaDriversData: getMegaDriversData(responseData),
        microDriversData: getMicroDriversData(responseData),
        topSpecificNeeds: getTopSpecificNeeds(responseData),
        highlyIndexedNeeds: getHighlyIndexedNeeds(responseData),
        timeChartData: getTimeChartData(responseData),
        timeTotalChartData: getTimeTotalChartData(responseData),
        timeChartSummaryData: getTimeChartSummaryData(responseData),
        sequence: getSequenceData(responseData),
        cravingsData: getCravingsData(responseData),
        foodTypeConsumedData: getFoodTypeConsumedData(responseData),
        atHomeEventsData: getAtHomeEventsData(responseData),
        awayFromHomeEventsData: getAwayFromHomeEventsData(responseData),
    };
}

export function getInfoFromAppData(appData, id) {
    let categoryName = '', categoryLink = 0, data = {}, renderCategoryTitle = () => {
    };
    const asSegment = appData.segments.find(item => item.id === id);
    const asOccasion = appData.occasions.find(item => item.id === id);
    const asTrigger = appData.triggers.find(item => item.id === id);
    const asDriver = appData.drivers.find(item => item.id === id);
    if (asSegment) {
        categoryName = 'Segment';
        categoryLink = 'the-decision';
        data = asSegment;
        renderCategoryTitle = (value) => (
            <>
                {value}% OF EATING EVENTS INVOLVE <span>{data.name}</span>
            </>
        );
    } else if (asOccasion) {
        categoryName = 'Occasion';
        categoryLink = 'occasions';
        data = asOccasion;
        renderCategoryTitle = (value) => (
            <>
                <span>{data.name}</span> ACCOUNTS FOR {value}% OF EATING EVENTS
            </>
        );
    } else if (asTrigger) {
        categoryName = 'Trigger';
        categoryLink = 'triggers';
        data = asTrigger;
        renderCategoryTitle = (value) => (
            <>
                {value}% OF EATING EVENTS ARE TRIGGERED BY <span>{data.name}</span>
            </>
        );
    } else if (asDriver) {
        categoryName = 'Driver';
        categoryLink = 'needs';
        data = asDriver;
        renderCategoryTitle = (value) => (
            <>
                {value}% OF EATING EVENTS ARE MOTIVATED BY <span>{data.name}</span>
            </>
        );
    }

    return {
        ...data,
        categoryName,
        categoryLink,
        renderCategoryTitle,
    };
}